import Vue from 'vue'
import { normalize } from 'normalizr'
import dayjs from 'dayjs'
import {
  S,
  G,
  M,
  A,
  MedicineGroupedList,
  MedicineGroups,
  SemeEffectMedicineGroupList,
  DispensationWeeklyGroupList
} from './types'
import { Getters, Mutations, Actions } from 'vuex'
import { Dispensation } from '@/types/dispensation'
import { dispensationSchema } from '@/schema/dispensation'
import { DispensationEntities } from '@/schema/dispensation/types'

/* -------------------------------------------------------------------------- */

const state = (): S => ({
  dispensations: {},
  medicines: {},
  sort: []
})

/* -------------------------------------------------------------------------- */

const getters: Getters<S, G> = {
  dispensations: state => state.dispensations,
  medicines: state => state.medicines,
  sort: state => state.sort,

  medicinesGroupedByRp: state =>
    state.sort.reduce((dispensations, dispensationId) => {
      const { medicines } = state.dispensations[dispensationId]

      const medicineGroups = medicines.reduce((medicineGroups, medicineId) => {
        const medicine = state.medicines[medicineId]
        const rpNo = medicine.rpNo

        if (!medicineGroups[rpNo]) {
          medicineGroups[rpNo] = []
        }

        medicineGroups[rpNo].push(medicine)

        return medicineGroups
      }, {} as MedicineGroups) as MedicineGroups

      Object.keys(medicineGroups).forEach(rpNo => {
        medicineGroups[Number(rpNo)] = medicineGroups[Number(rpNo)].sort(
          (m1, m2) => m1.seq - m2.seq
        )
      })

      return Object.assign(dispensations, {
        [dispensationId]: medicineGroups
      })
    }, {} as MedicineGroupedList),

  sameEffectMedicineGroups: ({ sort, dispensations, medicines }) => {
    return sort.reduce((groups, id) => {
      const medicineIds = dispensations[id].medicines
      const dispensationDayOfYear = dayjs(
        dispensations[id].dispensationDate
      ).dayOfYear()

      return medicineIds.reduce((groups, id) => {
        const { medicineCode, daysTimes } = medicines[id]
        const key = medicineCode.substr(0, 7)

        if (!groups[key]) {
          groups[key] = {}
        }

        if (!groups[key][medicineCode]) {
          groups[key][medicineCode] = []
        }

        groups[key][medicineCode].push({
          start: dispensationDayOfYear,
          end: dispensationDayOfYear + daysTimes
        })

        return groups
      }, groups)
    }, {} as SemeEffectMedicineGroupList)
  },

  dispensationWeeklyGroups: ({ sort, dispensations, medicines }) => {
    return sort.reduce((groups, id) => {
      const dispensationDate = dayjs(dispensations[id].dispensationDate)
      const medicineIds = dispensations[id].medicines
      const week = dispensationDate.isoWeek()

      return medicineIds.reduce((groups, id) => {
        const {
          daysTimes,
          dose,
          diffMark,
          medicineCode,
          classification,
          medicineName
        } = medicines[id]
        if (!groups[medicineCode]) {
          groups[medicineCode] = {
            classification,
            medicineName,
            medicines: {}
          }
        }
        ;[...new Array(Math.ceil(daysTimes / 7))].forEach((_, index) => {
          if (week + index > 53) {
            return
          }
          groups[medicineCode].medicines[week + index] = {
            index,
            daysTimes,
            dose,
            diffMark,
            dispensationDate
          }
        })
        return groups
      }, groups)
    }, {} as DispensationWeeklyGroupList)
  },

  calenderSort: ({ sort, dispensations, medicines }) => {
    const items = sort.reduce((items, id) => {
      const medicineIds = dispensations[id].medicines
      const day = dayjs(dispensations[id].dispensationDate).dayOfYear()

      medicineIds.forEach(id => {
        const { seq, medicineCode } = medicines[id]

        if (!items[medicineCode] || items[medicineCode].day > day) {
          items[medicineCode] = { seq, day }
        }
      })

      return items
    }, {} as { [code: string]: { seq: number; day: number } })

    return Object.keys(items)
      .sort((m1, m2) => items[m1].seq - items[m2].seq)
      .sort((m1, m2) => items[m1].day - items[m2].day)
  }
}

/* -------------------------------------------------------------------------- */

const mutations: Mutations<S, M> = {
  setDispensations: (state, { dispensations, medicines, sort }) => {
    state.dispensations = dispensations
    state.medicines = medicines
    state.sort = sort
  },

  removeDispensation: (state, { dispensationId }) => {
    if (state.dispensations[dispensationId]) {
      state.sort.splice(state.sort.indexOf(dispensationId), 1)
      state.dispensations[dispensationId].medicines.forEach(id =>
        Vue.delete(state.medicines, id)
      )
    }
    Vue.delete(state.dispensations, dispensationId)
  }
}

/* -------------------------------------------------------------------------- */

const actions: Actions<S, A, G, M> = {
  async fetch({ commit }, { patientId, year, isoYear }) {
    const res = await Vue.$api.dispensation.fetch({ patientId, year, isoYear })

    const { result, entities } = normalize<
      Array<Dispensation>,
      DispensationEntities,
      Array<number>
    >(res, [dispensationSchema])

    const { dispensations, dispensationMedicines } = entities

    commit('setDispensations', {
      dispensations,
      medicines: dispensationMedicines,
      sort: result
    })
  },

  async fetchOne({ commit }, { patientId, dispensationId }) {
    const res = await Vue.$api.dispensation.fetchOne({
      patientId,
      dispensationId
    })

    const { result, entities } = normalize<
      Dispensation,
      DispensationEntities,
      number
    >(res, dispensationSchema)

    const { dispensations, dispensationMedicines } = entities

    commit('setDispensations', {
      dispensations,
      medicines: dispensationMedicines,
      sort: [result]
    })
  },

  async patch({ commit }, { patientId, dispensationId, payload }) {
    const res = await Vue.$api.dispensation.patch({
      patientId,
      dispensationId,
      payload
    })

    const { result, entities } = normalize<
      Dispensation,
      DispensationEntities,
      number
    >(res, dispensationSchema)

    const { dispensations, dispensationMedicines } = entities

    commit('setDispensations', {
      dispensations,
      medicines: dispensationMedicines,
      sort: [result]
    })
  },

  async create({ commit }, { patientId, payload }) {
    const response = await Vue.$api.dispensation.create({ patientId, payload })

    const { result, entities } = normalize<
      Dispensation,
      DispensationEntities,
      number
    >(response, dispensationSchema)

    const { dispensations, dispensationMedicines } = entities

    commit('setDispensations', {
      dispensations,
      medicines: dispensationMedicines,
      sort: [result]
    })
  },

  async delete({ commit }, { patientId, dispensationId }) {
    await Vue.$api.dispensation.delete({ patientId, dispensationId })
    commit('removeDispensation', { dispensationId })
  }
}

/* -------------------------------------------------------------------------- */

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
